// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

// @mui material components
import { Autocomplete, Card, CircularProgress, Fade, Grid, Icon, InputLabel, MenuItem, Modal, Select, TextField, Tooltip } from "@mui/material";

// Apollo
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";

// UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

// Context
import { MessageManager } from "context";

// Utils
import { validateEmail, validatePhone } from "utils/validations";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "90%",
	width: { xs: "90%", sm: "60%", xxl: "50%" },
	overflowY: "auto",
	p: 3,
};

export default function ModalAddClienteEvento({ open, handleClose, refetch, selectedCliente }) {
	const { handleSnackbar } = useContext(MessageManager);
	const [cliente, setCliente] = useState();
	const [searchCliente, setSearchCliente] = useState("");
	const debounceRef = useRef(null);

	const handleChanges = (e) => {
		setCliente({
			...cliente,
			[e.target.name]: e.target.value,
		});
	};

	// Al cambiar el tipo de documento, si existe un valor en "documento" lo reseteamos a null para evitar inconsistencias
	const handleTipoDocumentoChange = (e) => {
		const value = e.target.value;
		setCliente((prev) => ({
			...(prev || {}),
			tipoDocumento: value,
			documento: prev?.documento ? null : prev?.documento ?? null,
		}));
	};

	const [saveClienteEvento, { loading: loadingSave }] = useMutation(
		gql`
      mutation saveClienteEvento($input: ClienteEventoInput!) {
        saveClienteEvento(input: $input) {
          id
        }
      }
    `
	);

	const { data: provinciasData } = useQuery(gql`
    query GetProvincias {
      provincias { id nombre }
    }
  `);

	const [getLocalidades, { data: localidadesData }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) { id nombre provincia { id } }
    }
  `);

	const [loadClientes, { data: clientesData, loading: loadingClientes }] = useLazyQuery(gql`
    query GET_CLIENTES($filter: JSON $search: String) {
      clientes(filter: $filter, search: $search) { id nombre apellido correo dni }
    }
  `);

	const clientesOptions = useMemo(() => clientesData?.clientes || [], [clientesData]);

	const handleSave = () => {
		saveClienteEvento({
			variables: {
				input: {
					id: cliente?.id || selectedCliente?.id || undefined,
					tipoCliente: cliente?.tipoCliente || undefined,
					razonSocial: cliente?.tipoCliente === "Empresa" ? (cliente?.razonSocial || null) : null,
					nombre: cliente?.tipoCliente === "Persona Física" ? (cliente?.nombre || null) : null,
					apellido: cliente?.tipoCliente === "Persona Física" ? (cliente?.apellido || null) : null,
					tipoDocumento: cliente?.tipoDocumento,
					documento: cliente?.documento,
					tipoResponsable: cliente?.tipoResponsable,
					idProvincia: cliente?.idProvincia ? parseInt(cliente?.idProvincia) : null,
					idLocalidad: cliente?.idLocalidad ? parseInt(cliente?.idLocalidad) : null,
					telefono: cliente?.telefono || undefined,
					email: cliente?.email || undefined,
					idCliente: Number(cliente?.idCliente) || undefined,
				},
			},
		})
			.then(() => {
				if (typeof refetch === "function") refetch();
				handleSnackbar("Cliente de evento guardado correctamente", "success");
				handleClose();
			})
			.catch(() => {
				handleSnackbar("Error al guardar el cliente de evento", "error");
			});
	};

	useEffect(() => {
		if (open) {
			if (selectedCliente) {
				setCliente({
					id: selectedCliente.id,
					tipoCliente: selectedCliente.tipoCliente || "Persona Física",
					razonSocial: selectedCliente.razonSocial ?? "",
					nombre: selectedCliente.nombre ?? "",
					apellido: selectedCliente.apellido ?? "",
					tipoDocumento: selectedCliente.tipoDocumento ?? 96,
					documento: selectedCliente.documento ?? "",
					tipoResponsable: selectedCliente.tipoResponsable ?? -1,
					idProvincia: selectedCliente.idProvincia ?? null,
					idLocalidad: selectedCliente.idLocalidad ?? null,
					telefono: selectedCliente.telefono || "",
					email: selectedCliente.email || "",
					idCliente: selectedCliente.idCliente ?? null,
				});
			} else {
				// Defaults al abrir para nuevo cliente de evento
				setCliente({
					idProvincia: 20,
					idLocalidad: 1815,
				});
			}
		} else {
			setCliente();
		}
	}, [open, selectedCliente]);

	const emailError = cliente?.email?.length > 0 && !validateEmail(cliente?.email);
	const telefonoError = cliente?.telefono?.length > 0 && !validatePhone(cliente?.telefono);

	useEffect(() => {
		if (cliente?.idProvincia) {
			getLocalidades({ variables: { provincia: cliente.idProvincia } });
		}
		if( cliente?.tipoCliente === "Persona Física") {
			setCliente((prev) => ({
				...(prev || {}),
				razonSocial: "",
			}));
		} else if( cliente?.tipoCliente === "Empresa") {
			setCliente((prev) => ({
				...(prev || {}),
				nombre: "",
				apellido: "",
			}));
		}
	}, [cliente?.idProvincia, cliente?.tipoCliente, cliente?.tipoDocumento, getLocalidades]);

	useEffect(() => {
		if (!open) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			loadClientes({ variables: { filter: {}, search: searchCliente && searchCliente.length >= 2 ? searchCliente : null } });
		}, 300);
		return () => debounceRef.current && clearTimeout(debounceRef.current);
	}, [searchCliente, loadClientes, open]);

	const handleSelectCliente = (value) => {
		if (!value) return;
		setCliente((prev) => ({
			...(prev || {}),
			idCliente: value.id,
			nombre: value.nombre || "",
			apellido: value.apellido || "",
			documento: value.dni || "",
			email: value.correo || "",
			tipoDocumento: 96, // DNI
			tipoResponsable: 5, // Consumidor final
			razonSocial: "",
			tipoCliente: "Persona Física",
		}));
	};

	const isSaveDisabled =
		loadingSave ||
		!cliente?.tipoDocumento ||
		!cliente?.documento ||
		!cliente?.tipoResponsable ||
		// Reglas por tipoCliente
		(cliente?.tipoCliente === "Persona Física" && (!cliente?.nombre || !cliente?.apellido)) ||
		(cliente?.tipoCliente === "Empresa" && !cliente?.razonSocial) ||
		emailError ||
		telefonoError;

	return (
		<Modal aria-labelledby="modal-cliente-evento-title" open={open} onClose={handleClose} closeAfterTransition>
			<Fade in={open}>
				<Card sx={style}>
					<SoftBox>
						<SoftBox display="flex" justifyContent="space-between">
							<SoftBox>
								<SoftTypography variant="h6">
									{selectedCliente ? "Editar cliente de evento" : "Nuevo cliente de evento"}
								</SoftTypography>
								<SoftTypography component="label" variant="caption" color="secondary">
									(*) Campos obligatorios
								</SoftTypography>
							</SoftBox>
							<SoftBox display="flex" justifyContent="space-between">
								<SoftBox>
									<Tooltip title="Cerrar" placement="top">
										<Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
											close
										</Icon>
									</Tooltip>
								</SoftBox>
							</SoftBox>
						</SoftBox>

						<SoftBox py={2}>
							<Grid container spacing={2}>
								{/* Buscar cliente existente */}
								<Grid item xs={12}>
									<InputLabel htmlFor="clienteBuscar">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Buscar cliente existente
										</SoftTypography>
									</InputLabel>
									<Autocomplete
										loading={loadingClientes}
										options={clientesOptions}
										getOptionLabel={(option) => `${option?.nombre || ""} ${option?.apellido || ""} — ${option?.dni || ""} — ${option?.correo || ""}`.trim()}
										onChange={(e, value) => handleSelectCliente(value)}
										onInputChange={(e, value) => setSearchCliente(value)}
										renderInput={(params) => (
											<TextField {...params} placeholder="Buscar por nombre, DNI o correo" size="small" />
										)}
										noOptionsText={searchCliente?.length >= 2 ? "Sin resultados" : "Escriba al menos 2 caracteres"}
									/>
								</Grid>

								{/* Tipo de cliente */}
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="tipoCliente">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Tipo de cliente
											<SoftTypography variant="caption" fontWeight="bold" color="primary">&nbsp;*</SoftTypography>
										</SoftTypography>
									</InputLabel>
									<Select
										name="tipoCliente"
										value={cliente?.tipoCliente || "Persona Física"}
										onChange={handleChanges}
										fullWidth
									>
										<MenuItem value={"Persona Física"}>Persona Física</MenuItem>
										<MenuItem value={"Empresa"}>Empresa</MenuItem>
									</Select>
								</Grid>

								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="nombre">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											{cliente?.tipoCliente === "Empresa" ? "Razón Social" : "Nombre"}
											<SoftTypography variant="caption" fontWeight="bold" color="primary">&nbsp;*</SoftTypography>
										</SoftTypography>
									</InputLabel>
									{cliente?.tipoCliente === "Empresa" ? (
										<SoftInput
											label="razonSocial"
											placeholder="Ingrese la razón social"
											type="text"
											name="razonSocial"
											value={cliente?.razonSocial || ""}
											onChange={handleChanges}
										/>
									) : (
										<SoftInput
											label="nombre"
											placeholder="Ingrese el nombre"
											type="text"
											name="nombre"
											value={cliente?.nombre || ""}
											onChange={handleChanges}
										/>
									)}
								</Grid>

								{/* Apellido para Persona Física */}
								{cliente?.tipoCliente === "Persona Física" && (
									<Grid item xs={12} sm={6}>
										<InputLabel htmlFor="apellido">
											<SoftTypography component="label" variant="caption" fontWeight="bold">
												Apellido
												<SoftTypography variant="caption" fontWeight="bold" color="primary">&nbsp;*</SoftTypography>
											</SoftTypography>
										</InputLabel>
										<SoftInput
											label="apellido"
											placeholder="Ingrese el apellido"
											type="text"
											name="apellido"
											value={cliente?.apellido || ""}
											onChange={handleChanges}
										/>
									</Grid>
								)}

								{/* Tipo de documento */}
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="tipoDocumento">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Tipo de documento
											<SoftTypography variant="caption" fontWeight="bold" color="primary">&nbsp;*</SoftTypography>
										</SoftTypography>
									</InputLabel>
									<Select
										name="tipoDocumento"
										value={cliente?.tipoDocumento || 96}
										onChange={handleTipoDocumentoChange}
										fullWidth
									>
										<MenuItem value={-1} disabled>Seleccione uno</MenuItem>
										<MenuItem value={96}>DNI</MenuItem>
										<MenuItem value={80}>CUIT</MenuItem>
										<MenuItem value={86}>CUIL</MenuItem>
									</Select>
								</Grid>

								{/* Documento */}
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="documento">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Documento
											<SoftTypography variant="caption" fontWeight="bold" color="primary">&nbsp;*</SoftTypography>
										</SoftTypography>
									</InputLabel>
									<SoftInput
										label="documento"
										placeholder="Ingrese el número"
										type="text"
										name="documento"
										value={cliente?.documento || ""}
										onChange={handleChanges}
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="telefono">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Teléfono
										</SoftTypography>
									</InputLabel>
									<Tooltip
										placement="bottom"
										open={telefonoError}
										title={telefonoError ? "Ingrese un teléfono válido (10-11 dígitos)" : ""}
									>
										<SoftInput
											label="telefono"
											placeholder="Ej: 3512345678"
											type="text"
											name="telefono"
											value={cliente?.telefono || ""}
											error={telefonoError}
											onChange={handleChanges}
										/>
									</Tooltip>
								</Grid>

								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="email">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Email
										</SoftTypography>
										<SoftTypography variant="caption" fontWeight="bold" color="primary">
											&nbsp;*
										</SoftTypography>
									</InputLabel>
									<Tooltip placement="bottom" open={emailError} title={emailError ? "Ingrese un email válido" : ""}>
										<SoftInput
											label="email"
											placeholder="cliente@correo.com"
											type="text"
											name="email"
											value={cliente?.email || ""}
											error={emailError}
											onChange={handleChanges}
										/>
									</Tooltip>
								</Grid>

								{/* Provincia */}
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="idProvincia">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Provincia
										</SoftTypography>
									</InputLabel>
									<Select
										name="idProvincia"
										value={cliente?.idProvincia || ""}
										onChange={(e) => {
											// reset localidad al cambiar provincia
											setCliente({ ...(cliente || {}), idProvincia: e.target.value, idLocalidad: null });
										}}
										fullWidth
									>
										<MenuItem value="">
											<em>Seleccione una provincia</em>
										</MenuItem>
										{(provinciasData?.provincias || []).map((p) => (
											<MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
										))}
									</Select>
								</Grid>

								{/* Localidad */}
								<Grid item xs={12} sm={6}>
									<InputLabel htmlFor="idLocalidad">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Localidad
										</SoftTypography>
									</InputLabel>
									<Select
										name="idLocalidad"
										value={cliente?.idLocalidad || ""}
										onChange={handleChanges}
										fullWidth
										disabled={!cliente?.idProvincia}
									>
										<MenuItem value="">
											<em>{cliente?.idProvincia ? "Seleccione una localidad" : "Seleccione provincia primero"}</em>
										</MenuItem>
										{(localidadesData?.localidades || []).map((l) => (
											<MenuItem key={l.id} value={l.id}>{l.nombre}</MenuItem>
										))}
									</Select>
								</Grid>

								<Grid item xs={6}>
									<InputLabel htmlFor="tipoResponsable">
										<SoftTypography component="label" variant="caption" fontWeight="bold">
											Situación frente al IVA
											<SoftTypography variant="caption" fontWeight="bold" color="primary">
												&nbsp;*
											</SoftTypography>
										</SoftTypography>
									</InputLabel>
									<Select
										name="tipoResponsable"
										value={cliente?.tipoResponsable || -1}
										onChange={handleChanges}
									>
										<MenuItem value={-1} disabled>
											Seleccione uno
										</MenuItem>
										<MenuItem value={1}>IVA Responsable Inscripto</MenuItem>
										<MenuItem value={2}>IVA Responsable no Inscripto</MenuItem>
										<MenuItem value={3}>IVA no Responsable</MenuItem>
										<MenuItem value={4}>IVA Sujeto Exento</MenuItem>
										<MenuItem value={5}>Consumidor Final</MenuItem>
										<MenuItem value={6}>Responsable Monotributo</MenuItem>
										<MenuItem value={7}>Sujeto no Categorizado</MenuItem>
										<MenuItem value={8}>Proveedor del Exterior</MenuItem>
										<MenuItem value={9}>Cliente del Exterior</MenuItem>
										<MenuItem value={10}>IVA Liberado – Ley Nº 19.640</MenuItem>
										<MenuItem value={11}>IVA Responsable Inscripto – Agente de Percepción</MenuItem>
										<MenuItem value={12}>Pequeño Contribuyente Eventual</MenuItem>
										<MenuItem value={13}>Monotributista Social</MenuItem>
										<MenuItem value={14}>Pequeño Contribuyente Eventual Social</MenuItem>
									</Select>
								</Grid>
							</Grid>
						</SoftBox>

						<SoftBox
							display={{ xs: "flex-row", sm: "flex" }}
							justifyContent="space-between"
							alignItems="center"
						>
							<SoftBox></SoftBox>
							<SoftBox display={{ xs: "flex-row", sm: "flex" }} justifyContent="end" alignItems="center">
								<SoftBox mr={{ xs: 0, sm: 2 }} mt={{ xs: 2, sm: 0 }}>
									<SoftButton
										color="primary"
										circular
										onClick={handleClose}
										fullWidth={{ xs: true, sm: false }}
									>
										<Icon sx={{ fontWeight: "light" }}>cancel</Icon>
										&nbsp;Cancelar
									</SoftButton>
								</SoftBox>
								<SoftBox mt={{ xs: 2, sm: 0 }}>
									<SoftButton
										color="uproGreen"
										circular
										disabled={isSaveDisabled}
										onClick={handleSave}
										fullWidth={{ xs: true, sm: false }}
									>
										{loadingSave ? (
											<CircularProgress size={15} color="inherit" />
										) : (
											<Icon sx={{ fontWeight: "light" }}>save</Icon>
										)}
										&nbsp;Guardar
									</SoftButton>
								</SoftBox>
							</SoftBox>
						</SoftBox>
					</SoftBox>
				</Card>
			</Fade>
		</Modal>
	);
}

ModalAddClienteEvento.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	refetch: PropTypes.func,
	selectedCliente: PropTypes.object,
};
